/**
 * Read-only "Test Results Summary" for forms that carry `SampleTestAcceptance` inputs: one row per test with
 * its name, result and remarks, so a reviewer can scan the outcome before taking an approval action.
 *
 * Everything is read from the form's own data (the paired test selector's `__Record`, the result code and the
 * remarks address), never fetched, so it works on an offline device.
 */

const libTestResultOptions = require('./_TestResultOptions.js');
const libUserMarkdown = require('./_UserMarkdown.js');

const TEST_SELECTOR_INPUT_TYPES = [ 'MaterialsHiddenTestSelector', 'MaterialsTestSelector', 'MaterialsTestPicker' ];

const REMARKS_PREVIEW_LENGTH = 160;

// pict-section-form gives each section's container this id (no per-render UUID), so it is a stable link target.
const SECTION_CONTAINER_ID_PREFIX = 'Pict-Form-Container-';

/**
 * @typedef {Object} TestResultsSummaryRow
 * @property {string} TestName - The test's display name.
 * @property {string} ResultCode - The stored result code, or '' when unset.
 * @property {string} ResultText - The result's display text, or '' when unset.
 * @property {string} Remarks - The remarks as plain text, or ''.
 * @property {string} SectionAnchorID - Element id of the test's form section, or '' when there is none to link to.
 */

/**
 * @typedef {Object} TestResultsSummaryContext
 * @property {Record<string, any>} Descriptors - The form's descriptors.
 * @property {Array<Record<string, any>>} [Sections] - The form's sections, used for a fallback test name.
 * @property {Record<string, any>} Data - The marshal destination (the form data).
 * @property {(pObject: any, pAddress: string) => any} getValueAtAddress - Address resolver (pict manifest).
 * @property {(pInput: Record<string, any>) => string|null} [getRemarksAddress] - The acceptance provider's remarks resolver.
 * @property {Document} [Document] - Used to flatten markdown remarks to text.
 */

/**
 * @param {any} pValue
 * @return {boolean}
 */
function isFilled(pValue)
{
	return (pValue !== null) && (typeof(pValue) !== 'undefined') && (String(pValue).trim() !== '');
}

/**
 * @param {Record<string, any>} pInput - The acceptance descriptor.
 * @param {any} pCode - The stored code.
 * @return {string}
 */
function getResultText(pInput, pCode)
{
	if (!isFilled(pCode))
	{
		return '';
	}
	const tmpOptions = pInput?.PictForm?.SelectOptions;
	if (Array.isArray(tmpOptions))
	{
		const tmpOption = tmpOptions.find((pOption) => pOption && pOption.id === pCode);
		if (tmpOption && tmpOption.text)
		{
			return String(tmpOption.text);
		}
	}
	return String(libTestResultOptions.getTestResultLabel(pCode));
}

/**
 * Remarks are user-typed markdown; flatten them through the hardened renderer into an inert document so the
 * summary shows readable text with no markup and nothing executable.
 *
 * @param {any} pMarkdown
 * @param {Document} [pDocument]
 * @return {string}
 */
function getRemarksText(pMarkdown, pDocument)
{
	if (!isFilled(pMarkdown))
	{
		return '';
	}
	let tmpText = String(pMarkdown);
	const TmpDOMParser = pDocument?.defaultView?.DOMParser;
	if (TmpDOMParser)
	{
		const tmpHTML = libUserMarkdown.renderUserMarkdown(tmpText);
		tmpText = new TmpDOMParser().parseFromString(tmpHTML, 'text/html').body.textContent || '';
	}
	tmpText = tmpText.replace(/\s+/g, ' ').trim();
	if (tmpText.length > REMARKS_PREVIEW_LENGTH)
	{
		tmpText = `${tmpText.slice(0, REMARKS_PREVIEW_LENGTH - 1).trimEnd()}…`;
	}
	return tmpText;
}

/**
 * @param {TestResultsSummaryContext} pContext
 * @param {Record<string, any>} pInput - The acceptance descriptor.
 * @param {Array<Record<string, any>>} pDescriptors
 * @return {string}
 */
function getTestName(pContext, pInput, pDescriptors)
{
	const tmpTrackingGUID = pInput.PictForm.TestTrackingGUID;
	if (tmpTrackingGUID && tmpTrackingGUID !== true)
	{
		const tmpSelector = pDescriptors.find((pDescriptor) => pDescriptor?.PictForm?.TestTrackingGUID === tmpTrackingGUID &&
			TEST_SELECTOR_INPUT_TYPES.indexOf(pDescriptor.PictForm.InputType) >= 0);
		if (tmpSelector && tmpSelector.DataAddress)
		{
			const tmpName = pContext.getValueAtAddress(pContext.Data, `${tmpSelector.DataAddress}__Record.Name`);
			// A placeholder test record is named after its hash; the section heading reads better.
			if (isFilled(tmpName) && String(tmpName) !== String(tmpTrackingGUID))
			{
				return String(tmpName);
			}
		}
	}
	const tmpSection = (pContext.Sections || []).find((pSection) => pSection && pSection.Hash === pInput.PictForm.Section);
	if (tmpSection && isFilled(tmpSection.Name))
	{
		return String(tmpSection.Name);
	}
	if (tmpTrackingGUID && tmpTrackingGUID !== true)
	{
		return String(tmpTrackingGUID);
	}
	return String(pInput.Name || pInput.Hash || '');
}

/**
 * One row per non-tabular `SampleTestAcceptance` input, in manifest order.
 *
 * @param {TestResultsSummaryContext} pContext
 * @return {Array<TestResultsSummaryRow>}
 */
function collectTestResults(pContext)
{
	const tmpDescriptors = Object.values(pContext.Descriptors || {});
	const tmpRows = [];
	for (const tmpInput of tmpDescriptors)
	{
		const tmpPictForm = tmpInput?.PictForm;
		if (!tmpPictForm || libTestResultOptions.TEST_RESULT_INPUT_TYPES.indexOf(tmpPictForm.InputType) < 0)
		{
			continue;
		}
		if (tmpPictForm.InformaryContainerAddress || !tmpInput.DataAddress)
		{
			continue;
		}
		const tmpCode = pContext.getValueAtAddress(pContext.Data, tmpInput.DataAddress);
		const tmpRemarksAddress = pContext.getRemarksAddress ? pContext.getRemarksAddress(tmpInput) : null;
		const tmpRemarks = tmpRemarksAddress ? pContext.getValueAtAddress(pContext.Data, tmpRemarksAddress) : null;
		tmpRows.push(
			{
				TestName: getTestName(pContext, tmpInput, tmpDescriptors),
				ResultCode: isFilled(tmpCode) ? String(tmpCode) : '',
				ResultText: getResultText(tmpInput, tmpCode),
				Remarks: getRemarksText(tmpRemarks, pContext.Document),
				SectionAnchorID: tmpPictForm.Section ? `${SECTION_CONTAINER_ID_PREFIX}${tmpPictForm.Section}` : '',
			});
	}
	return tmpRows;
}

/**
 * The summary shows once any test has a result.
 *
 * @param {Array<TestResultsSummaryRow>} pRows
 * @return {boolean}
 */
function hasAnyResult(pRows)
{
	return pRows.some((pRow) => pRow.ResultCode !== '');
}

/**
 * @param {string} pCode
 * @return {string} - Bulma tag modifiers for the result.
 */
function getResultTagClass(pCode)
{
	switch (pCode)
	{
	case 'PASS':
	case 'PASC':
		return 'is-success is-soft';
	case 'FAIL':
	case 'FAIC':
		return 'is-danger is-soft';
	case 'PROG':
	case 'PEND':
		return 'is-warning is-soft';
	default:
		return '';
	}
}

/**
 * @param {Document} pDocument
 * @param {string} pTag
 * @param {string} [pClass]
 * @param {string} [pText]
 * @return {HTMLElement}
 */
function createElement(pDocument, pTag, pClass, pText)
{
	const tmpElement = pDocument.createElement(pTag);
	if (pClass)
	{
		tmpElement.className = pClass;
	}
	if (typeof(pText) === 'string')
	{
		tmpElement.textContent = pText;
	}
	return tmpElement;
}

/**
 * @param {Document} pDocument
 * @param {Array<TestResultsSummaryRow>} pRows
 * @return {HTMLElement}
 */
function buildSummaryElement(pDocument, pRows)
{
	const tmpFilledCount = pRows.filter((pRow) => pRow.ResultCode !== '').length;

	const tmpBox = createElement(pDocument, 'div', 'test-results-summary');
	const tmpHeading = createElement(pDocument, 'div', 'test-results-summary-heading');
	tmpHeading.append(createElement(pDocument, 'h2', 'title is-6 mb-0', 'Test Results Summary'));
	tmpHeading.append(createElement(pDocument, 'span', 'is-size-7 has-text-grey', `${tmpFilledCount} of ${pRows.length} ${pRows.length === 1 ? 'test has a result' : 'tests have results'}`));
	tmpBox.append(tmpHeading);

	const tmpScroller = createElement(pDocument, 'div', 'table-container');
	const tmpTable = createElement(pDocument, 'table', 'table is-fullwidth is-narrow');
	const tmpHeadRow = createElement(pDocument, 'tr');
	for (const tmpLabel of [ 'Test', 'Result', 'Remarks' ])
	{
		tmpHeadRow.append(createElement(pDocument, 'th', '', tmpLabel));
	}
	const tmpHead = createElement(pDocument, 'thead');
	tmpHead.append(tmpHeadRow);
	tmpTable.append(tmpHead);

	const tmpBody = createElement(pDocument, 'tbody');
	for (const tmpRow of pRows)
	{
		const tmpTR = createElement(pDocument, 'tr');
		const tmpNameCell = createElement(pDocument, 'td', 'test-results-summary-name');
		if (tmpRow.SectionAnchorID && pDocument.getElementById(tmpRow.SectionAnchorID))
		{
			const tmpLink = createElement(pDocument, 'a', '', tmpRow.TestName);
			// A bare fragment is what the PDF printer turns into an in-document jump (no URL, so no session token).
			// On screen the page's <base href> would send it to another URL, so the click scrolls instead.
			tmpLink.setAttribute('href', `#${tmpRow.SectionAnchorID}`);
			tmpLink.addEventListener('click', (pEvent) =>
			{
				const tmpTarget = pDocument.getElementById(tmpRow.SectionAnchorID);
				if (!tmpTarget || pEvent.button !== 0 || pEvent.metaKey || pEvent.ctrlKey || pEvent.shiftKey || pEvent.altKey)
				{
					return;
				}
				pEvent.preventDefault();
				tmpTarget.scrollIntoView({ block: 'start' });
			});
			tmpNameCell.append(tmpLink);
		}
		else
		{
			tmpNameCell.textContent = tmpRow.TestName;
		}
		tmpTR.append(tmpNameCell);
		const tmpResultCell = createElement(pDocument, 'td', 'test-results-summary-result');
		if (tmpRow.ResultCode)
		{
			tmpResultCell.append(createElement(pDocument, 'span', `tag ${getResultTagClass(tmpRow.ResultCode)}`.trim(), tmpRow.ResultText));
		}
		else
		{
			tmpResultCell.append(createElement(pDocument, 'span', 'has-text-grey', '—'));
		}
		tmpTR.append(tmpResultCell);
		tmpTR.append(createElement(pDocument, 'td', 'test-results-summary-remarks', tmpRow.Remarks));
		tmpBody.append(tmpTR);
	}
	tmpTable.append(tmpBody);
	tmpScroller.append(tmpTable);
	tmpBox.append(tmpScroller);
	return tmpBox;
}

/**
 * Ask the app's test results summary view, when it registered one, to redraw from this form view's data.
 *
 * @param {Record<string, any>} pPict
 * @param {Record<string, any>} pView - The form section view the change or marshal came from.
 */
function requestSummaryRefresh(pPict, pView)
{
	const tmpSummary = pPict && pPict.views && pPict.views.TestResultsSummary;
	if (tmpSummary && pView && typeof pView.getMarshalDestinationObject === 'function')
	{
		tmpSummary.requestRefresh(pView.getMarshalDestinationObject());
	}
}

module.exports = { collectTestResults, hasAnyResult, buildSummaryElement, getRemarksText, getResultTagClass, requestSummaryRefresh };
